import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Crown, Check, Loader2, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";

const PREMIUM_FEATURES = [
  "Unlimited AI research summaries, assistant chat & briefings (fair-use limits)",
  "\u201cWhy did my portfolio fall today?\u201d, AI digest & risk reads",
  "Scanner custom filters + plain-English match reasons",
  "Monte Carlo outcome bands in the Wealth Simulator",
  "Dividend income projection for your portfolio",
  "Unlimited watchlists, portfolios & holdings",
  "Price / RSI alerts on watchlists",
  "Shareable research cards",
];

export default function Upgrade() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const confirmed = useRef(false);

  const load = useCallback(() => api.get("/billing/status").then((r) => setStatus(r.data)), []);
  useEffect(() => { load(); }, [load]);

  // Return leg from PayPal approval: verify server-side, never trust the redirect alone
  useEffect(() => {
    const subId = params.get("subscription_id");
    if (params.get("paypal") === "success" && subId && !confirmed.current) {
      confirmed.current = true;
      setBusy(true);
      api.post("/billing/confirm", { subscription_id: subId })
        .then((r) => {
          toast.success(r.data.message);
          window.location.replace("/upgrade"); // full reload so tier refreshes everywhere
        })
        .catch((e) => {
          toast.error(apiErrorDetail(e));
          setBusy(false);
          setParams({});
        });
    } else if (params.get("paypal") === "cancelled") {
      toast.info("PayPal checkout cancelled — you have not been charged.");
      setParams({});
    }
  }, [params, setParams]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/subscribe");
      window.location.href = data.approval_url; // approve on PayPal
    } catch (e) {
      toast.error(apiErrorDetail(e));
      setBusy(false);
    }
  };

  const cancelSub = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/cancel");
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <Skeleton className="h-96 rounded-sm bg-card" />;
  const isPremium = status.tier === "premium";

  return (
    <div className="max-w-3xl space-y-6 fade-up" data-testid="upgrade-page">
      <div>
        <div className="overline-label mb-2">Your plan</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white flex items-center gap-3">
          {isPremium ? "You're Premium" : "Go Premium"} <Crown className="w-8 h-8 text-yellow-500" />
        </h1>
      </div>

      {!status.paypal_configured && !isPremium && (
        <div className="border border-yellow-500/40 bg-yellow-500/10 rounded-sm px-4 py-3 flex items-start gap-3"
          data-testid="paypal-unconfigured-notice">
          <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-200/90">
            Payments are not configured yet. The owner needs to add PayPal credentials
            (PAYPAL_CLIENT_ID / PAYPAL_CLIENT_SECRET) — until then, subscribing is unavailable.
          </p>
        </div>
      )}

      {!isPremium && (
        <div className="widget p-5" data-testid="trial-usage-card">
          <div className="flex items-center justify-between mb-2">
            <span className="overline-label flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-primary" /> Free AI trial</span>
            <span className="font-mono text-xs text-muted-foreground" data-testid="trial-usage-count">
              {status.trial_used} / {status.trial_limit} lifetime AI calls used
            </span>
          </div>
          <Progress value={(status.trial_used / status.trial_limit) * 100} className="h-2" />
          {status.trial_used >= status.trial_limit && (
            <p className="text-xs text-loss mt-2">Trial exhausted — upgrade to keep using AI research.</p>
          )}
        </div>
      )}

      <div className={`widget p-8 ${isPremium ? "border-yellow-500/40" : "border-primary/50"}`} data-testid="pricing-card">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="font-heading font-bold text-white tracking-tight text-xl">SphereInvest AI Premium</div>
            <div className="text-sm text-muted-foreground mt-1">Everything in Free, plus the full research toolkit</div>
          </div>
          <div className="font-mono text-white">
            <span className="text-4xl font-medium" data-testid="premium-price">£{status.price_gbp}</span>
            <span className="text-muted-foreground text-sm"> / month</span>
          </div>
        </div>
        <ul className="space-y-2.5 mb-8">
          {PREMIUM_FEATURES.map((f, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-gray-300">
              <Check className="w-4 h-4 text-gain shrink-0 mt-0.5" /> {f}
            </li>
          ))}
        </ul>

        {isPremium ? (
          <div className="space-y-3" data-testid="premium-active-panel">
            <p className="text-sm text-gain font-mono">✓ Premium active</p>
            {status.subscription?.status === "ACTIVE" && (
              <>
                {status.subscription.next_billing_time && (
                  <p className="text-xs text-muted-foreground font-mono">
                    Next billing: {new Date(status.subscription.next_billing_time).toLocaleDateString()}
                  </p>
                )}
                <Button onClick={cancelSub} disabled={busy} variant="outline"
                  className="rounded-sm border-border text-muted-foreground hover:text-loss" data-testid="cancel-subscription-button">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel subscription"}
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  Cancelling keeps Premium until the end of the period you've paid for.
                </p>
              </>
            )}
            {status.subscription?.premium_until && (
              <p className="text-xs text-yellow-500 font-mono">
                Cancelled — Premium until {new Date(status.subscription.premium_until).toLocaleDateString()}
              </p>
            )}
          </div>
        ) : (
          <Button onClick={subscribe} disabled={busy || !status.paypal_configured}
            className="w-full h-12 rounded-sm bg-[#ffc439] hover:bg-[#f2ba36] text-[#003087] font-bold text-base disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="paypal-subscribe-button">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>Subscribe with <span className="italic font-black ml-1.5">Pay</span><span className="italic font-black text-[#009cde]">Pal</span></>
            )}
          </Button>
        )}
        <p className="text-[11px] text-muted-foreground mt-4">
          {status.paypal_mode === "sandbox" && !isPremium ? "Payments run in PayPal sandbox mode. " : ""}
          Cancel any time. This platform provides AI-generated research, not financial advice.
        </p>
      </div>
    </div>
  );
}
